import React, { useState, useEffect } from 'react';
import { X, FileText, BarChart3, Target, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../services/api';

interface IssueCategoryCount {
  category: string;
  categoryDisplay: string;
  count: number;
  percentage: number;
}

interface ManifestoAnalysis {
  aspirantId: number;
  wardId: number;
  wardName: string;
  totalReports: number;
  issueCategoryCounts: IssueCategoryCount[];
  topIssueCategories: string[];
  averageUrgencyScore: number;
  pendingReports: number;
  resolvedReports: number;
}

interface GenerateManifestoModalProps {
  aspirantId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const GenerateManifestoModal: React.FC<GenerateManifestoModalProps> = ({
  aspirantId,
  onClose,
  onSuccess
}) => {
  const [title, setTitle] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [maxIssues, setMaxIssues] = useState(5);
  const [includeAllReports, setIncludeAllReports] = useState(true);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<ManifestoAnalysis | null>(null);

  useEffect(() => {
    fetchAnalysis();
  }, []);

  const fetchAnalysis = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/analysis`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch analysis');
      const data = await response.json();
      if (data.success) {
        setAnalysis(data.data);
      } else {
        // fallback mock data
        setAnalysis({
          aspirantId,
          wardId: 1,
          wardName: 'Central Ward',
          totalReports: 127,
          issueCategoryCounts: [
            { category: 'ROADS', categoryDisplay: 'Roads & Infrastructure', count: 45, percentage: 35.4 },
            { category: 'WATER', categoryDisplay: 'Water Supply', count: 32, percentage: 25.2 },
            { category: 'HEALTH', categoryDisplay: 'Healthcare', count: 22, percentage: 17.3 },
            { category: 'EDUCATION', categoryDisplay: 'Education', count: 15, percentage: 11.8 },
            { category: 'SECURITY', categoryDisplay: 'Security', count: 13, percentage: 10.3 }
          ],
          topIssueCategories: ['ROADS', 'WATER', 'HEALTH'],
          averageUrgencyScore: 7.2,
          pendingReports: 68,
          resolvedReports: 59
        });
      }
    } catch (error) {
      console.error('Analysis fetch failed, using mock:', error);
      setAnalysis({
        aspirantId,
        wardId: 1,
        wardName: 'Central Ward',
        totalReports: 127,
        issueCategoryCounts: [],
        topIssueCategories: [],
        averageUrgencyScore: 7.2,
        pendingReports: 68,
        resolvedReports: 59
      });
    }
  };

  const handleGenerate = async () => {
    if (!title.trim()) {
      toast.error('Please enter a manifesto title');
      return;
    }

    setLoading(true);
    try {
      const request = {
        title,
        customInstructions,
        maxIssuesToInclude: maxIssues,
        includeAllReports,
        specificReportIds: []
      };

      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/generate-from-reports`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please login again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('aspirant');
          setTimeout(() => window.location.href = '/login', 2000);
          return;
        }
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const responseData = await response.json();

      if (responseData.success) {
        toast.success('Manifesto generated successfully!');
        window.dispatchEvent(new CustomEvent('manifesto-created'));
        onSuccess();
        onClose();
      } else {
        throw new Error(responseData.error || 'Failed to generate manifesto');
      }
    } catch (error: any) {
      console.error('Generation error:', error);
      toast.error('Manifesto generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <BarChart3 className="text-primary-600 mr-3" size={24} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Generate Manifesto from Reports</h2>
              <p className="text-sm text-gray-600">Create manifesto based on citizen concerns</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Analysis Summary */}
          {analysis && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                <Target size={16} className="mr-2" />
                Ward Analysis Summary
              </h4>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-blue-700">Total Reports</p>
                  <p className="font-bold text-blue-900">{analysis.totalReports}</p>
                </div>
                <div>
                  <p className="text-blue-700">Top Issue</p>
                  <p className="font-bold text-blue-900">
                    {analysis.issueCategoryCounts?.[0]?.categoryDisplay || 'Infrastructure'}
                  </p>
                </div>
                <div>
                  <p className="text-blue-700">Resolved</p>
                  <p className="font-bold text-blue-900">{analysis.resolvedReports}</p>
                </div>
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Manifesto Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., My Plan for Ward Development"
            />
          </div>

          {/* Issues to Include */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Issues to Include
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="range"
                min="3"
                max="10"
                value={maxIssues}
                onChange={(e) => setMaxIssues(parseInt(e.target.value))}
                className="flex-1"
              />
              <span className="font-medium text-gray-900">{maxIssues} issues</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              This will include the top {maxIssues} most reported issues from citizen reports
            </p>
          </div>

          {/* Include All Reports */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="includeAll"
              checked={includeAllReports}
              onChange={(e) => setIncludeAllReports(e.target.checked)}
              className="h-4 w-4 text-primary-600 rounded"
            />
            <label htmlFor="includeAll" className="ml-2 text-sm text-gray-700">
              Include all citizen reports from your ward ({analysis?.totalReports || 0} reports)
            </label>
          </div>

          {/* Custom Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Instructions (Optional)
            </label>
            <textarea
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Add any specific instructions or additional commitments..."
            />
            <p className="text-sm text-gray-500 mt-1">
              These will be added to the generated manifesto
            </p>
          </div>

          {/* What to Expect */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <AlertCircle size={16} className="mr-2" />
              What will be generated?
            </h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Analysis of citizen reports in your ward
              </li>
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Commitment statements for each top issue
              </li>
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Statistics showing percentage of reports addressed
              </li>
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Vision and mission statements based on top issues
              </li>
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Custom instructions you add above
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={loading || !title.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <FileText size={18} className="mr-2" />
                Generate Manifesto
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateManifestoModal;